/**
 * useSpeedDatingRealtime - Hook for speed dating real-time events via Ably
 * 
 * Subscribes to:
 * - event_started: When host starts the event
 * - round_started: When a new round begins (with pair info)
 * - event_finished: When the event ends
 * - match_created: When a mutual like creates a match
 */

import { useEffect, useCallback, useRef } from 'react';
import { getRealtimeClient } from '../services/realtime';

/**
 * @param {string} eventId - Speed dating event ID to subscribe to
 * @param {Object} handlers - Event handlers
 * @param {(data: {round_index: number, round: object}) => void} [handlers.onEventStarted]
 * @param {(data: {round_index: number, pairs: array, started_at: string}) => void} [handlers.onRoundStarted]
 * @param {() => void} [handlers.onEventFinished]
 * @param {(data: {match_id: string, partner_id: string}) => void} [handlers.onMatchCreated]
 */
export default function useSpeedDatingRealtime(eventId, handlers = {}) {
  const channelRef = useRef(null);
  const handlersRef = useRef(handlers);
  
  // Keep handlers ref up to date
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const subscribe = useCallback(async () => {
    if (!eventId) return;
    
    try {
      const client = await getRealtimeClient();
      const channelName = `speeddating:event:${eventId}`;
      const channel = client.channels.get(channelName);
      
      channelRef.current = channel;

      // Subscribe to event_started
      channel.subscribe('event_started', (message) => {
        console.log('[SpeedDating RT] Event started:', message.data);
        handlersRef.current.onEventStarted?.(message.data);
      });

      // Subscribe to round_started
      channel.subscribe('round_started', (message) => {
        console.log('[SpeedDating RT] Round started:', message.data);
        handlersRef.current.onRoundStarted?.(message.data);
      });

      // Subscribe to event_finished
      channel.subscribe('event_finished', (message) => {
        console.log('[SpeedDating RT] Event finished:', message.data);
        handlersRef.current.onEventFinished?.();
      });

      // Subscribe to match_created (mutual like)
      channel.subscribe('match_created', (message) => {
        console.log('[SpeedDating RT] Match created:', message.data);
        handlersRef.current.onMatchCreated?.(message.data);
      });

      console.log(`[SpeedDating RT] Subscribed to ${channelName}`);
    } catch (err) {
      console.error('[SpeedDating RT] Failed to subscribe:', err);
    }
  }, [eventId]);

  const unsubscribe = useCallback(() => {
    if (channelRef.current) {
      try {
        channelRef.current.unsubscribe();
        console.log('[SpeedDating RT] Unsubscribed');
      } catch (err) {
        console.error('[SpeedDating RT] Failed to unsubscribe:', err);
      }
      channelRef.current = null;
    }
  }, []);

  useEffect(() => {
    subscribe();
    return () => unsubscribe();
  }, [subscribe, unsubscribe]);

  return { subscribe, unsubscribe };
}
